import React, { useEffect, useRef, useMemo, useState, useContext, useCallback } from "react"

import { useSpring } from "@react-spring/three"
import * as THREE from 'three'

import { AppContext } from '../../context'
import { usePageHighlight } from '../../context/pageHighlightContext'

import { easeCubic } from "d3-ease"

import { Plane, ImagePlane, Svg } from "./plane"
import { debounce } from "lodash"

import hoverTextImg from '../../assets/hover-text.svg';

const GenericElement = (_props) => {
    const { name, size, ratio, position = [0,0,0], rotation = [0,0,0], pageOffset = 0, interactivable, nonClickable, sounds = [], from, hover, loop, reset, reverse, duration, linkPage, overlay, opacity = 1, sub, animation, ...props } = _props;
    const [isHover, setHover] = useState(false)
    const [active, setActive] = useState(false)
    const [isSelected, setIsSelected] = useState(false)
    const [hidden, setHidden] = useState(false)
    const [blokHover, setBlockHover] = useState(false)
    const { selection, setSelection, setCurrent, setZoomDelta, setHtmlOverlay, allowZoomIn,  suspendedControls } = useContext(AppContext);

    const [shouldAnim, setShouldAnim] = useState(false)

    const onRest = useCallback(() => {
        if (active && Number.isInteger(linkPage)) {
            setActive(false)
            setCurrent(linkPage);
        } else {
            setShouldAnim(active);            
        }
        setBlockHover(false);
    }, [active, linkPage, setCurrent]);

    const showHovered = isHover && !blokHover ;

    const {size: activeSize, ratio: activeRatio, zoomFactor=1, ambient} = props.keyart || {size,ratio}
    const width = active ? activeSize * activeRatio : size * ratio;
    const height = active ? activeSize : size;
    const scaleFactor = active ? 1.8 * zoomFactor : showHovered ? 1.1 : 1;

    const { ...anim } = useSpring(animation ? animation : {
        config: { duration: active || !interactivable || duration ? duration : shouldAnim ? 160 : 80, easing: easeCubic },
        to: !active && showHovered && hover && !hidden ? hover : {
            scale: [width * scaleFactor, height* scaleFactor, 1],
            position: active ? [-pageOffset, 0, 1] : showHovered ? [position[0], position[1], 0.1] : position,
            rotation: active ? [0, 0, 0] : rotation,
            opacity: hidden ? 0 : opacity
        },
        loop,
        from,
        reset,
        reverse,
        onRest,        
    });

    const ref = useRef()
    
    useEffect(() => {
        ref.current.name = name;
    }, [name]);

    useEffect(() => {
        if (!interactivable) return;

        //console.log('czeck to set selection', name);    
        if (active) {
            if (Number.isInteger(linkPage)) {
                setHover(false);
            } else if (!isSelected) {
                setSelection(ref.current);
                setIsSelected(true);
            }
        } else if (selection === ref.current) {
            setSelection(null);
        }
    }, [active, interactivable, linkPage, selection]);

    useEffect(() => {
        setHidden(selection && selection !== ref.current && !sub);

        if (!interactivable) return;

        if (selection !== ref.current) {            
            active && setBlockHover(true);
            setActive(false)
            setHover(false);
            setIsSelected(false);
        }

    }, [selection, interactivable, sub]);

    const onSingleClick = useCallback(e => {
        //   console.log('test onSingleClick', name, (!interactivable || active || hidden));

        if (!interactivable || active || hidden) return;
        e.stopPropagation();

        //console.log('go nSingleClick', name, linkPage);
        setShouldAnim(true);
        setActive(!!props.keyart);

        if (linkPage !== undefined) {
            setCurrent(linkPage);
        }
        if (overlay) {
            setHtmlOverlay(overlay.text);
        }
    }, [active, hidden, interactivable, linkPage,
       props.keyart, overlay, setCurrent, setHtmlOverlay, sounds]);

    const onDoubleClick = useCallback(e => {
        e.stopPropagation();
        //console.log('onDoubleClick', name, e, allowZoomIn);
        setZoomDelta({ delta: allowZoomIn ? 0.15 : -0.15 });
    }, [allowZoomIn, setZoomDelta]);

    const EPS = 10.0;
    const clickCountRef = useRef(0);
    const pointerPositionRef = useRef(null);

    useEffect (()=> {
        pointerPositionRef.current = [-100, -100];
    }, [suspendedControls])

    const onDown = useCallback(e => {
        if (!interactivable) return;
        //    console.log('DOWN DELTA', name, [e.clientX, e.clientY]);
        pointerPositionRef.current = [e.clientX, e.clientY];
    }, [interactivable])

    const resolveClick = debounce((e) => {
        //     console.log('setTimeout', name, clickCountRef.current, e);
        if (clickCountRef.current === 1) onSingleClick(e); else if (clickCountRef.current >= 2) onDoubleClick(e);
        clickCountRef.current = 0;
    }, 300, { 'maxWait': 300 });

    const onClick = useCallback(e => {        
        if (interactivable) {
            e.stopPropagation();
        }
        if (pointerPositionRef.current) {
            const [x, y] = pointerPositionRef.current;
            const a = new THREE.Vector2(x, y);
            const b = new THREE.Vector2(e.clientX, e.clientY);
            const delta = a.distanceTo(b);
            pointerPositionRef.current = null;
           // console.log('onClick DELTA', name, delta, a, b);
            if (delta > EPS) {
                console.log('onClick skip', name, clickCountRef.current, e);
                return;
            }
        }
        clickCountRef.current += 1;
        resolveClick(e);
    }, [allowZoomIn, name, onDoubleClick, onSingleClick, interactivable]);

    const onHover = useCallback(e => {
        if (!interactivable) return;  
        e.stopPropagation();
        setHover(true);
    }, [interactivable, active])

    const onUnhover = useCallback(e => {               
        if (!interactivable) return;
        setHover(false);
    }, [interactivable])

    return (
        <>
            <Plane args={[1, 1, 32, 32]} frustumCulled={false}
                onClick={props.svg || nonClickable ? undefined : onClick}
                onPointerOver={props.svg || nonClickable ? undefined : onHover}
                onPointerOut={props.svg || nonClickable ? undefined : onUnhover}
                onPointerDown={props.svg || nonClickable ? undefined : onDown}
                ref={ref}
                hovered={isHover}
                active={active}
                name={name}
                interactivable={interactivable}
                {...props}
                {...anim}
            />
            {props.svg && <Svg
                {...props.svg}
                position={position}
                onClick={onClick}
                onPointerOver={onHover}
                onPointerOut={onUnhover}
                onPointerDown={onDown}
            />}
            {props.highlight && <TextHighlightElement position={position} args={[size * ratio, size, 32, 32]} hovered={isHover}/>}
        </>
    )
}

export const TextHighlightElement = ({ position, hovered, args }) => {
    const { opacity } = useSpring({ opacity: hovered ? 1 : 0 })
    const offsetX = args[0]/2 - 0.025;
    const offsetY = args[1]/2 + 0.25;
    const ref = useRef();

    return <group position={position}>
        <Svg
            ref={ref} frustumCulled={false}
            url={hoverTextImg}
            fillOpacity={opacity}
            groupScale={[0.008, 0.008, 1]}
            position={[offsetX,-offsetY,0]}
            color={"#02d7f2"} 
        />
        <Svg
            ref={ref} frustumCulled={false}
            url={hoverTextImg}
            fillOpacity={opacity}
            groupScale={[0.008, 0.008, 1]}
            position={[offsetX,-offsetY,0]}
            flipY= {true}
            color={"#02d7f2"} 
        /> 
        <Svg
            ref={ref} frustumCulled={false}
            url={hoverTextImg}
            fillOpacity={opacity}
            groupScale={[0.008, 0.008, 1]}
            position={[offsetX,-offsetY,0]}
            flipX= {true}
            color={"#02d7f2"} 
        />
        <Svg
            ref={ref} frustumCulled={false}
            url={hoverTextImg}
            fillOpacity={opacity}
            groupScale={[0.008, 0.008, 1]}
            position={[offsetX,-offsetY,0]}
            flipX= {true}
            flipY= {true}
            color={"#02d7f2"} 
        />    
    </group>
}

export const PageHighlightElement = ({ name, size, ratio, index }) => {
    const { pageStep, advancedMode, selection } = useContext(AppContext);
    const [ pageHighlight, setPageHighlight] = usePageHighlight();

    const ref = useRef();
    
    const [visible, setVisible] = useState(false);
    const [hover, setHover] = useState(false);
    const { opacity } = useSpring({
        config: { duration: 160, easing: easeCubic },
        to: { opacity: visible ? 1 : 0 }
    })
    const width = size * ratio;

    useEffect(() => {
        setVisible(hover && advancedMode && pageStep > 1 && !selection);
    }, [hover, advancedMode, selection]);

    useEffect(() => {
        setHover(pageHighlight === name);
    }, [pageHighlight, name]);

    const onHover = useCallback(isOver => e => {
        setPageHighlight(isOver ? name : null);
    }, [setPageHighlight])

    return <>
    <ImagePlane 
        ref={ref}
        name={name} args={[(size + 0.03) * ratio,  0.06, 32, 32]} frustumCulled={false}
        opacity={opacity}
        position={[(index ? 1 : -1) * 0.03,size * 0.502,-0.0011]}   
        color={"#02d7f2"} 
    />
    <ImagePlane 
        ref={ref}
        name={name} args={[(size + 0.03) * ratio,  0.06, 32, 32]} frustumCulled={false}
        opacity={opacity}
        position={[(index ? 1 : -1) * 0.03,-size * 0.502 ,-0.0011]}   
        color={"#02d7f2"} 
    />
    <ImagePlane 
        ref={ref}
        name={name} args={[ 0.05 * ratio,  size + 0.1, 32, 32]} frustumCulled={false}
        opacity={opacity}
        position={[(index ? 1 : -1) * ((size + 0.03) * ratio * 0.5)  ,0,-0.0011]}   
        color={"#02d7f2"} 
    />    

    <ImagePlane 
        ref={ref}
        name={name} args={[width, size, 32, 32]} frustumCulled={false}
        opacity={0}
        position={[0,2.0*size * 0.5 ,0]}   
        onPointerOver={onHover(false)}
    />

    <ImagePlane 
        ref={ref}
        name={name} args={[width, size, 32, 32]} frustumCulled={false}
        opacity={0}
        position={[0,-2.0*size * 0.5 ,0]}   
        onPointerOver={onHover(false)}
    />

    <ImagePlane 
        ref={ref}
        name={name} args={[width, 3* size, 32, 32]} frustumCulled={false}
        opacity={0}
        position={[(index ? 1 : -1) * width, 0 ,0]}   
        onPointerOver={onHover(false)}
    />


    <ImagePlane 
        name={name} args={[width, size, 32, 32]} frustumCulled={false}
        onPointerOver={onHover(true)}
        opacity={0}
        position={[0,0,0]} 
        scale={[1,1,1]} 
    />
    </>
}

export default (props) => {
    return <GenericElement  {...props} />
};